// Contexto vivo de Benito — lo que vuelve el acompañamiento CIERTO.
//
// Hasta ahora Benito era ciego al momento del usuario: no sabía la hora, ni si
// estaba en turno, ni cuántas camas llevaba, ni su racha. Todo eso ya existe en
// la app; estos helpers PUROS lo destilan en tres piezas, sin costo de IA:
//   1. contextoAhora()  → bloque [Ahora: …] que se inyecta al agente en la
//      primera vuelta (junto a capa y memoria), para que responda sabiendo
//      dónde está parado el usuario.
//   2. saludoBenito()   → el saludo de apertura del panel, armado con el
//      estado real (turno en curso, cierre pendiente, días fuera) — local,
//      determinista, cero tokens.
//   3. chipsBenito()    → chips de arranque que ofrecen lo pertinente AL
//      MOMENTO (cerrar el turno, retomar el último tema) en vez de un menú
//      fijo.
//
// REGLA DE ORO (misma que resumen_paciente en agenteTools.js): solo hechos de
// catálogo cerrado — camas, contadores, fases, horas, tipos de tarea. NUNCA
// texto libre del turno (notas de entrada, motivo de aislamiento, detalle de
// una tarea): puede traer nombre/RUT/relato del paciente y no debe llegar ni
// al modelo ni al saludo. Y tono anti-culpa: los hechos se ofrecen, no acusan
// (una racha rota o una evolución pendiente jamás se le echa en cara).

import { calcularTurnoDelDia } from '../utils/calendario';
import { faseDelTurno, turnoAnclaPostNoche, TIPO_TURNO_LABEL } from './turnoCockpit';
import { tieneContenido } from './evolucionSistema';
import { examenTieneContenido } from './examenFisico';
import { proximaTarea, sanitizarCama } from './registroTurno';
import { diasEntre, fechaKey } from './turnoRacha';
import { rachaViva } from './rachaDiaria';
import { saludoDieciocho, chipDieciocho } from './dieciocho';
import { UNIVERSIDAD_LABEL, SERVICIO_LABEL } from './perfilCatalogos';
import { PREFILL_ELEGIR_MODELO } from './paeModelos';

// ── El turno que rige AHORA ───────────────────────────────────────────────────
// El de hoy si es operativo, o el de ayer si el usuario viene saliendo (o sigue
// dentro) de una noche/24h — la misma ancla que usa la fase del cierre, para
// que Benito hable del MISMO turno que ve el resto de la app. Devuelve
// { turno, fase } o null si hoy no hay turno que valga ("libre" no es turno:
// decirle "vas en tu turno" a alguien en su día libre rompe la confianza).
// `overrideHoy` (fila de turno_overrides, opcional): el turno que el usuario
// cambió a mano HOY manda sobre el patrón — sin él, el saludo afirmaba un
// turno que el calendario ya no tiene (barrido 2026-08-02).
export function turnoVigente(rotacion, ahora = new Date(), overrideHoy = null) {
  if (!rotacion) return null;
  const ancla = turnoAnclaPostNoche(rotacion, ahora);
  if (ancla) return { turno: ancla.turno, fase: faseDelTurno(ancla.turno, ahora, ancla.base) };
  const turnoHoy = calcularTurnoDelDia(ahora, rotacion, overrideHoy) || null;
  if (!turnoHoy || turnoHoy.tipo === 'libre' || !turnoHoy.horaInicio) return null;
  return { turno: turnoHoy, fase: faseDelTurno(turnoHoy, ahora) };
}

// ── Resumen del turno desde los registros LOCALES ─────────────────────────────
// Mismo criterio que el hub de MiPAE en turno (tieneContenido para "con evolución").
// Excluye cerrados y la cama de práctica (la demo no es un paciente: contarla
// haría que Benito hable de un turno que no existe). De la próxima tarea toma
// SOLO hora/tipo/cama — el `detalle` es texto libre y se queda fuera.
export function resumenTurnoVivo(registros = [], ahora = new Date()) {
  const activos = (Array.isArray(registros) ? registros : []).filter(
    (r) => r && !r.cerrado && !r.practica,
  );
  const camas = activos.map((r) => sanitizarCama(r.cama || '')).filter(Boolean);
  const nConEvolucion = activos.filter((r) => tieneContenido(r.evolucion)).length;
  const nConExamen = activos.filter((r) => examenTieneContenido(r.examenFisico)).length;
  // Camas sin examen de ingreso — para que Benito pueda OFRECER (sobrio) ayudar
  // a registrarlo, nunca para acusar (anti-culpa: el silencio no se reprocha).
  const camasSinExamen = activos.filter((r) => !examenTieneContenido(r.examenFisico))
    .map((r) => sanitizarCama(r.cama || '')).filter(Boolean);
  const p = proximaTarea(activos, ahora);
  return {
    nPacientes: activos.length,
    nConEvolucion,
    nConExamen,
    camas,
    camasSinExamen,
    proximaTarea: p ? { hora: p.hora, tipo: p.tipo, cama: p.cama } : null,
  };
}

// ── Días sin venir ────────────────────────────────────────────────────────────
// Del mapa local de días activos (rachaDiaria.diasActivosLocal — device-local,
// sin viaje a la BD). Null si no hay historia (usuario nuevo o dispositivo
// nuevo): sin datos no se saluda "de vuelta" a nadie.
export function diasFuera(diasActivos = [], hoy = fechaKey()) {
  if (!Array.isArray(diasActivos) || diasActivos.length === 0) return null;
  const ultimo = diasActivos[diasActivos.length - 1];
  const gap = diasEntre(ultimo, hoy);
  return gap == null ? null : Math.max(0, gap);
}

// ── Bloque [Ahora: …] para el agente ──────────────────────────────────────────
// Se inyecta como preludio (par user/assistant) en la primera vuelta, igual que
// la capa y la memoria — nunca en el SYSTEM, que se cachea. Devuelve '' si no
// hay ningún hecho que aporte (el bloque vacío solo gastaría tokens).
export function contextoAhora({
  ahora = new Date(),
  vigente = null,        // { turno, fase } de turnoVigente()
  resumen = null,        // de resumenTurnoVivo()
  camaFoco = null,       // cama que el usuario está mirando en MiPAE en turno
  racha = null,          // { racha, fecha, escudos } de cargarRachaDiaria()
  fueraDias = null,      // de diasFuera()
} = {}) {
  const hechos = [];

  if (vigente?.turno) {
    const label = TIPO_TURNO_LABEL[vigente.turno.tipo] || 'Turno';
    const horario = vigente.turno.horaInicio && vigente.turno.horaFin
      ? ` (${vigente.turno.horaInicio}–${vigente.turno.horaFin})`
      : '';
    const faseTxt = {
      antes: 'aún no empieza',
      durante: 'está en curso',
      cierre: 'ya terminó, está en la fase de cerrar y entregar',
    }[vigente.fase] || '';
    if (faseTxt) hechos.push(`${label}${horario}: ${faseTxt}.`);
  }

  if (resumen && resumen.nPacientes > 0) {
    const camasTxt = resumen.camas.length ? ` (camas ${resumen.camas.join(', ')})` : '';
    // Conteo neutro del examen de ingreso (simétrico con la evolución): informa,
    // no acusa — un 0 con examen NO se subraya como falta.
    const examenTxt = Number.isFinite(resumen.nConExamen) ? `; ${resumen.nConExamen} con examen de ingreso` : '';
    hechos.push(
      `En MiPAE en turno lleva ${resumen.nPacientes} paciente(s)${camasTxt}; ${resumen.nConEvolucion} con evolución armada${examenTxt}.`,
    );
    if (resumen.proximaTarea) {
      const t = resumen.proximaTarea;
      hechos.push(`Su próxima tarea pendiente: ${t.tipo} a las ${t.hora}${t.cama ? ` para el paciente de la cama ${t.cama}` : ''}.`);
    }
  }

  if (camaFoco) hechos.push(`Ahora mismo está con su paciente de la cama ${camaFoco} en MiPAE en turno.`);

  // La racha solo se menciona si está viva y ya es un hábito (≥2 días). Una
  // racha rota NO se reporta: el modelo no tiene por qué saberlo (anti-culpa).
  if (racha && Number(racha.racha) >= 2 && rachaViva(racha.fecha, racha.escudos, fechaKey(ahora))) {
    hechos.push(`Lleva ${racha.racha} días seguidos activo en MiPAE.`);
  }

  if (fueraDias != null && fueraDias >= 3) {
    hechos.push(`Vuelve a la app después de ${fueraDias} días (dale la bienvenida sin reproche).`);
  }

  if (!hechos.length) return '';
  const hh = String(ahora.getHours()).padStart(2, '0');
  const mm = String(ahora.getMinutes()).padStart(2, '0');
  return [
    `[Ahora (${hh}:${mm}): estado real del usuario en MiPAE. Úsalo para acompañar con contexto cuando venga al caso; no lo recites ni lo repitas entero:`,
    ...hechos.map((h) => `- ${h}`),
    ']',
  ].join('\n');
}

// "HH:MM" del calendario o null: jamás se imprime una hora que no venga en
// ese formato (un jsonb adulterado no puede saludar con basura).
function horaValida(h) {
  return typeof h === 'string' && /^\d{1,2}:\d{2}$/.test(h) ? h : null;
}

// ── Saludo de apertura ────────────────────────────────────────────────────────
// `saludoHora` viene del caller (getSaludoPorHora de Home) para no importar un
// componente desde una lib. Las variantes contextuales omiten el "Soy Benito"
// (a esa altura ya se presentó: la presentación de primera vez lo garantiza);
// la genérica lo conserva tal cual era.
export function saludoBenito({
  saludoHora = 'Hola.',
  vigente = null,
  resumen = null,
  fueraDias = null,
  libreHoy = null,
  turnoManana = null,
  horaTurnoManana = null,
  fecha = undefined,
} = {}) {
  // El Dieciocho manda por sobre el saludo de contexto: es la excepción de la
  // fecha, dura tres días y se retira sola (saludoDieciocho devuelve null fuera
  // de ventana, así que abajo no cambia nada el resto del año). `libreHoy` y
  // `turnoManana` solo se pasan cuando el llamador conoce la rotación: sin
  // ellos cae el saludo neutro, que no afirma nada del turno de nadie.
  const dieciocho = saludoDieciocho({
    fase: vigente?.fase ?? null,
    libreHoy,
    turnoManana,
    ...(fecha ? { fecha } : {}),
  });
  if (dieciocho) return `${saludoHora} ${dieciocho}`;

  const n = resumen?.nPacientes || 0;
  if (vigente?.fase === 'durante') {
    if (n > 0) {
      const pacientesTxt = n === 1 ? 'un paciente' : `${n} pacientes`;
      return `${saludoHora} Vas en tu turno con ${pacientesTxt}. Aquí estoy si necesitas anotar, calcular o revisar a un paciente.`;
    }
    return `${saludoHora} ¿En pleno turno? Aquí estoy: puedo anotar por ti, calcular o buscarte lo que necesites.`;
  }
  if (vigente?.fase === 'cierre' && n > 0) {
    return `${saludoHora} ¿Saliendo del turno? Si quieres te ayudo a cerrarlo: qué falta, la evolución o la entrega.`;
  }
  // Próximo turno con su hora real (auditoría editorial 2026-09-28, E12):
  // "está por partir" sonaba a inminente aunque faltaran 18 horas. `vigente`
  // en fase 'antes' es SIEMPRE el turno de hoy (turnoVigente no mira más
  // allá), así que "hoy" es exacto; la hora sale del calendario. Mañana solo
  // se afirma cuando el llamador la conoce (rotación + overrides), y sin hora
  // no se inventa: cae al saludo neutro.
  if (vigente?.fase === 'antes') {
    const hora = horaValida(vigente?.turno?.horaInicio);
    return hora
      ? `${saludoHora} Tu próximo turno es hoy a las ${hora}. ¿Quieres preparar algo?`
      : `${saludoHora} Tu próximo turno es hoy. ¿Quieres preparar algo?`;
  }
  if (!vigente && turnoManana && horaValida(horaTurnoManana)) {
    return `${saludoHora} Tu próximo turno es mañana a las ${horaValida(horaTurnoManana)}. ¿Quieres preparar algo?`;
  }
  if (fueraDias != null && fueraDias >= 3) {
    return `${saludoHora} Qué bueno verte de vuelta. ¿En qué te doy una mano?`;
  }
  return `${saludoHora} Soy Benito, ¿en qué te doy una mano?`;
}

// ── Chips de arranque ─────────────────────────────────────────────────────────
// Reglas de manejarChip en CompaneroGlobal: un chip cuyo texto termina en ':'
// o espacio, o que trae `prefill: true`, queda de PREFILL (el usuario lo
// completa o lo revisa); el resto se envía solo.
// Máximo 3: es una oferta, no un menú. `temaAnterior` (el último mensaje del
// usuario en un hilo ya vencido) va primero — retomar es el gesto de un
// compañero que recuerda.
// Cada chip lleva `id` de catálogo cerrado: es lo ÚNICO que viaja a la
// telemetría (el texto puede llevar tema/cama del usuario, el id jamás).
export function chipsBenito({
  vigente = null,
  resumen = null,
  temaAnterior = null,
  elegibleTurno = true,
  tens = false,
  fecha = undefined,
} = {}) {
  const chips = [];
  if (temaAnterior) {
    chips.push({ id: 'retomar', t: `Sigamos con lo que hablábamos: ${temaAnterior}`, l: 'Seguir donde quedamos' });
  }
  // Un solo chip de la fecha, y va antes que los de siempre porque es lo que
  // caduca. Los habituales siguen abajo: el slice(0, 3) del final decide.
  const chipFecha = chipDieciocho({ elegibleTurno, ...(fecha ? { fecha } : {}) });
  if (chipFecha) chips.push(chipFecha);

  const n = resumen?.nPacientes || 0;
  if (tens) {
    // TENS (D15, 4-oct-2026): sus chips son SU registro y la consulta, jamás
    // el PAE (cuidados delegados y observación, frontera de lib/tens.js). Va
    // antes que las fases: los chips del turno del enfermero (anotar en la
    // ficha, el examen al recibir) no son su superficie.
    chips.push(
      { id: 'tens_registro', t: 'Llévame a MiPAE en turno para registrar mis cuidados', l: 'Mi registro del turno' },
      { id: 'escalas', t: 'Llévame a las escalas', l: 'Llévame a las escalas' },
      { id: 'como_funciona', t: '¿Cómo funciona MiPAE? Dame el mapa corto', l: '¿Cómo funciona MiPAE?' },
    );
  } else if (vigente?.fase === 'cierre' && n > 0) {
    chips.push(
      { id: 'cierre_falta', t: '¿Qué me falta para cerrar el turno?', l: '¿Qué me falta del turno?' },
      { id: 'resumen_turno', t: 'Resume mi turno', l: 'Resume mi turno' },
    );
  } else if (vigente?.fase === 'durante' && n > 0) {
    // Con una sola cama, el chip de anotar ya sabe cuál es (menos que tipear).
    const anota = resumen.camas.length === 1
      ? { id: 'anotar_cama', t: `Anota para el paciente de la cama ${resumen.camas[0]}: `, l: `Anotar · cama ${resumen.camas[0]}` }
      : { id: 'anotar_cama', t: 'Anota para el paciente de la cama ', l: 'Anotar a un paciente' };
    // Oferta sobria del examen de ingreso: si hay una cama sin él, el tercer chip
    // lleva a hacerlo (el documento guía a examinar temprano). Es una oferta —
    // nunca un popup, nunca reproche. Si están todas, el chip de siempre.
    const camaSinEx = resumen.camasSinExamen?.[0];
    const tercero = camaSinEx
      // "al recibir", jamás "de ingreso" (#536 y dictado 2026-08-10: "no se
      // entiende"). El id queda igual: es catálogo de telemetría.
      ? { id: 'examen_ingreso', t: `Llévame a hacer el examen físico al recibir del paciente de la cama ${camaSinEx}`, l: `Examen al recibir · cama ${camaSinEx}` }
      : { id: 'dilucion', t: 'Dilución de ', l: 'Dilución de…' };
    chips.push({ id: 'resumen_turno', t: 'Resume mi turno', l: 'Resume mi turno' }, anota, tercero);
  } else if (!elegibleTurno) {
    // Capa formación sin turno: su día es el PAE y el estudio (P4, decisión
    // del fundador 2026-10-04).
    //  - "¿Qué modelo uso en mi PAE?" deja escrita la MISMA pregunta del
    //    "¿Cuál elijo?" del PAE (PREFILL_ELEGIR_MODELO) y queda de prefill
    //    (`prefill: true`): la persona la revisa y la envía.
    //  - "Pregunta de repaso" ya no le pide a la IA que invente una pregunta:
    //    lleva al reto del día, que sale del banco revisado (abrir_modulo).
    //  - "Llévame a las escalas" salió de aquí para dejar lugar (máximo 3);
    //    las escalas siguen a un toque en la Enciclopedia.
    chips.push(
      { id: 'pae', t: 'Quiero armar un PAE', l: 'Armar un PAE' },
      { id: 'modelo_pae', t: PREFILL_ELEGIR_MODELO, l: '¿Qué modelo uso en mi PAE?', prefill: true },
      { id: 'repaso', t: 'Llévame al reto del día', l: 'Pregunta de repaso' },
    );
  } else if (n === 0) {
    // Enfermero sin camas registradas (nuevo, o arrancando el día): el empuje
    // del fundador (31-jul) — Benito sugiere FUERTE ir a MiPAE en turno y ofrece el
    // mapa de la app a un toque (su system prompt ya lo tiene). Siguen siendo
    // chips del panel que el usuario abrió: Benito jamás se abre solo.
    chips.push(
      { id: 'empezar_turno', t: 'Llévame a MiPAE en turno para registrar mi turno', l: 'Empezar mi turno' },
      { id: 'como_funciona', t: '¿Cómo funciona MiPAE? Dame el mapa corto', l: '¿Cómo funciona MiPAE?' },
      { id: 'dilucion', t: 'Dilución de ', l: 'Dilución de…' },
    );
  } else {
    // Con camas pero sin turno vigente: el arranque de siempre.
    chips.push(
      { id: 'escalas', t: 'Llévame a las escalas', l: 'Llévame a las escalas' },
      { id: 'resumen_turno', t: 'Resume mi turno', l: 'Resume mi turno' },
      { id: 'dilucion', t: 'Dilución de ', l: 'Dilución de…' },
    );
  }
  return chips.slice(0, 3);
}


// ── Lo que Benito sabe del perfil (memoria contextual, 2026-10-02) ───────────
// Benito ya sabía la CAPA (estudiante, internado, profesional) por el bloque
// [Contexto]. Esto le suma lo que la persona ya declaró en su perfil, todo de
// catálogo cerrado: universidad, año de carrera y servicio. Así no tiene que
// preguntarlo ni la persona repetirlo. Va dentro del MISMO bloque [Contexto]
// (el tope del servidor reconoce a lo más tres pares de preludio). Gratis
// para todos: no guarda nada nuevo, usa lo que el perfil ya tiene. La
// Política de Privacidad lo declara (sección 2, Benito).
//
// Kill-switch ON por defecto. Un valor fuera de catálogo no se dice ("Otra
// universidad" y "Otro servicio" tampoco: no informan nada).
export function perfilBenitoHabilitado() {
  return process.env.REACT_APP_BENITO_PERFIL_ENABLED !== 'false';
}

export function contextoPerfil(profile) {
  if (!profile || !perfilBenitoHabilitado()) return '';
  const rol = profile.rol;
  const uni = profile.universidad && profile.universidad !== 'otra' ? UNIVERSIDAD_LABEL[profile.universidad] : null;
  const anio = Number.isInteger(profile.anio_carrera) && profile.anio_carrera >= 1 && profile.anio_carrera <= 7
    ? profile.anio_carrera : null;
  const servicio = profile.servicio && profile.servicio !== 'otro' ? SERVICIO_LABEL[profile.servicio] : null;
  if (rol === 'estudiante') {
    const partes = [];
    if (uni) partes.push(`estudia en ${uni}`);
    if (anio) partes.push(`está en ${anio}.º año`);
    if (servicio) partes.push(`hace su práctica en ${servicio}`);
    if (!partes.length) return '';
    const texto = partes.length > 1 ? `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}` : partes[0];
    return `Según su perfil, ${texto}.`;
  }
  if ((rol === 'enfermero' || rol === 'tens') && servicio) {
    return `Según su perfil, trabaja en ${servicio}.`;
  }
  return '';
}
